import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import {
  LOGIN_USER,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
  loginUserSuccess,
  loginUserError,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
  changePasswordSuccess,
  changePasswordError,
  setProducts,
  setActivePid,
  normalizeAuthPayload,
} from '../actions';

import { adminRoot } from '../../constants/defaultValues';
import { callApi, buildAuthHeaders, withPostPutDeleteAsync } from '../api';
import { persistor } from '../store';

const getApiErrorMessage = (error, fallback) =>
  error?.message || error?.error || fallback;

const loginWithEmailPasswordAsync = (email, password) =>
  callApi('api/user/login', {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      email,
      password,
    }),
  });

const fetchProductsAsync = (token) =>
  callApi('api/products?page=1&limit=10', {
    headers: buildAuthHeaders(token),
  });

function* loginWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;
  try {
    const loginResult = yield call(
      loginWithEmailPasswordAsync,
      email,
      password
    );

    if (loginResult?.status !== 'success' || !loginResult?.access_token) {
      yield put(
        loginUserError(
          loginResult?.message || loginResult?.error || 'Login failed'
        )
      );
      return;
    }

    const authPayload = normalizeAuthPayload(loginResult);
    yield put(loginUserSuccess(authPayload));

    const productsResult = yield call(fetchProductsAsync, authPayload.token);
    const products = productsResult?.data || [];
    const productsTotal = productsResult?.pagination?.total ?? products.length;
    yield put(setProducts(products, productsTotal));

    if (productsTotal === 0 || products.length === 0) {
      yield put(loginUserError('auth.noProducts'));
      return;
    }

    const product = products[0];
    yield put(
      setActivePid(product.pid, product.role || '', product.name || '')
    );
    history.push(adminRoot);
  } catch (error) {
    yield put(loginUserError(error?.message || error?.error || 'Login failed'));
  }
}

export function* watchLoginUser() {
  yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

const logoutAsync = async () => {
  await persistor.purge();
  window.location.reload();
};

function* logout() {
  yield call(logoutAsync);
}

export function* watchLogoutUser() {
  yield takeEvery(LOGOUT_USER, logout);
}

const forgotPasswordAsync = (email) =>
  callApi('api/user/forgot-password', {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ email }),
  });

function* forgotPassword({ payload }) {
  const { email } = payload.forgotUserMail || {};
  try {
    const result = yield call(forgotPasswordAsync, email);
    if (result?.success) {
      yield put(forgotPasswordSuccess());
      return;
    }
    yield put(
      forgotPasswordError(
        getApiErrorMessage(result, 'user.forgot.error.generic')
      )
    );
  } catch (error) {
    if (error?.status === 503) {
      yield put(forgotPasswordError('user.forgot.error.smtp'));
      return;
    }
    if (error?.status === 422) {
      yield put(forgotPasswordError('user.forgot.error.email-required'));
      return;
    }
    yield put(
      forgotPasswordError(
        getApiErrorMessage(error, 'user.forgot.error.generic')
      )
    );
  }
}

export function* watchForgotPassword() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

const resetPasswordAsync = (token, newPassword) =>
  callApi('api/user/reset-password', {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      token,
      new_password: newPassword,
    }),
  });

function* resetPassword({ payload }) {
  const { token, newPassword, history } = payload;
  try {
    yield call(resetPasswordAsync, token, newPassword);
    yield put(resetPasswordSuccess());
    if (history) {
      history.push('/user/login?passwordReset=1');
    }
  } catch (error) {
    if (error?.status === 400) {
      yield put(resetPasswordError('user.reset.error.invalid-token'));
      return;
    }
    if (error?.status === 422) {
      yield put(resetPasswordError('user.reset.error.password-short'));
      return;
    }
    yield put(
      resetPasswordError(getApiErrorMessage(error, 'user.reset.error.generic'))
    );
  }
}

export function* watchResetPassword() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

function* changePassword({ payload }) {
  const { currentPassword, newPassword } = payload;
  try {
    const token = yield select(
      (state) => state.authUser?.currentUser?.token || ''
    );
    yield call(withPostPutDeleteAsync, {
      query: 'api/user/change-password',
      method: 'POST',
      token,
      body: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    });
    yield put(changePasswordSuccess());
    // Backend revokes all sessions; clear local tokens and force re-login.
    yield call([persistor, 'purge']);
    window.location.assign('/user/login?passwordChanged=1');
  } catch (error) {
    if (error?.status === 400) {
      yield put(changePasswordError('user.change.error.current-incorrect'));
      return;
    }
    if (error?.status === 403) {
      yield put(changePasswordError('user.change.error.forbidden'));
      return;
    }
    if (error?.status === 422) {
      yield put(changePasswordError('user.change.error.validation'));
      return;
    }
    yield put(
      changePasswordError(
        getApiErrorMessage(error, 'user.change.error.generic')
      )
    );
  }
}

export function* watchChangePassword() {
  yield takeEvery(CHANGE_PASSWORD, changePassword);
}

export default function* rootSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogoutUser),
    fork(watchForgotPassword),
    fork(watchResetPassword),
    fork(watchChangePassword),
  ]);
}
